import React, { useRef, useEffect, useState } from 'react'
import { PlayIcon, PauseIcon, SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/react/24/solid'
import { motion } from 'framer-motion'

interface HeroVideoProps {
  title: string
  subtitle?: string
  showControls?: boolean
  height?: 'sm' | 'md' | 'lg' | 'xl'
  overlay?: 'light' | 'dark' | 'none'
}

const HeroVideo: React.FC<HeroVideoProps> = ({ 
  title, 
  subtitle, 
  showControls = true,
  height = 'md',
  overlay = 'dark'
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const [isMuted, setIsMuted] = useState(true)
  const [isLoading, setIsLoading] = useState(true)

  const heightClasses = {
    sm: 'h-64 md:h-80',
    md: 'h-80 md:h-96',
    lg: 'h-96 md:h-[32rem]',
    xl: 'h-[32rem] md:h-[40rem]'
  }

  const overlayClasses = {
    light: 'bg-white/20',
    dark: 'bg-black/40',
    none: ''
  }

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      // Auto-play the video when component mounts
      video.muted = true // Ensure video is muted before attempting to play
      setIsMuted(true)
      
      // Try to play the video after a short delay
      setTimeout(() => {
        const playPromise = video.play()
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            // If autoplay fails, set playing state to false
            setIsPlaying(false)
          })
        }
      }, 1000)
      
      const handleLoadedData = () => setIsLoading(false)
      const handlePlay = () => setIsPlaying(true)
      const handlePause = () => setIsPlaying(false)
      
      video.addEventListener('loadeddata', handleLoadedData)
      video.addEventListener('play', handlePlay)
      video.addEventListener('pause', handlePause)
      
      return () => {
        // Cleanup event listeners
        if (video) {
          video.removeEventListener('loadeddata', handleLoadedData)
          video.removeEventListener('play', handlePlay)
          video.removeEventListener('pause', handlePause)
          
          // Pause video on cleanup to prevent AbortError
          if (!video.paused) {
            video.pause()
          }
        }
      }
    }
  }, [])

  const togglePlay = () => {
    const video = videoRef.current
    if (video) {
      if (isPlaying) {
        video.pause()
      } else {
        video.play()
      }
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (video) {
      video.muted = !video.muted
      setIsMuted(video.muted)
    }
  }

  return (
    <div className={`relative w-full ${heightClasses[height]} overflow-hidden`}>
      {/* Video Background */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        loop
        muted={isMuted}
        playsInline
        preload="metadata"
      >
        <source src="/keeneland-racing-video.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-rb-dark flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400"></div>
        </div>
      )}

      {/* Overlay */}
      {overlay !== 'none' && (
        <div className={`absolute inset-0 ${overlayClasses[overlay]}`} />
      )}

      {/* Content */}
      <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xl md:text-2xl text-white/90 drop-shadow-md max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>
      </div>

      {/* Video Controls */}
      {showControls && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-4 right-4 z-20 flex space-x-2"
        >
          <button
            onClick={togglePlay}
            className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all duration-200 backdrop-blur-sm"
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? (
              <PauseIcon className="w-5 h-5" />
            ) : (
              <PlayIcon className="w-5 h-5" />
            )}
          </button>
          <button
            onClick={toggleMute}
            className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all duration-200 backdrop-blur-sm"
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          >
            {isMuted ? (
              <SpeakerXMarkIcon className="w-5 h-5" />
            ) : (
              <SpeakerWaveIcon className="w-5 h-5" />
            )}
          </button>
        </motion.div>
      )}

      {/* Keeneland-style bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/60 to-transparent z-5" />
    </div>
  )
}

export default HeroVideo
